import React, { useState, useEffect } from "react";
import {
  ArrowLeft,
  Edit,
  User,
  MapPin,
  ShieldCheck,
  Mail,
  Phone,
  Calendar,
  Briefcase,
  Loader2,
  CreditCard,
  Clock,
  Building2,
  AlertCircle,
  CheckCircle,
  XCircle,
  BadgeCheck,
  Hash,
  Wallet,
  FileText,
  ChefHat,
  IndianRupee,
  Percent,
  CalendarDays,
} from "lucide-react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import useEmployee from "@/hooks/useEmployee";
import employeeService from "@/services/employeeService";

const TABS = [
  { id: "basic", label: "Basic Info", icon: User },
  { id: "employment", label: "Employment Details", icon: Briefcase },
  { id: "address", label: "Address & Emergency", icon: MapPin },
  { id: "auth", label: "Auth & Access", icon: ShieldCheck },
  { id: "bank", label: "Bank Details", icon: CreditCard },
  // { id: "documents", label: "Documents", icon: FileText }, // Hidden for now
  { id: "payroll", label: "Payroll Info", icon: Wallet },
  { id: "leave", label: "Leave Balance", icon: CalendarDays },
  { id: "shift", label: "Shift & Stations", icon: Clock },
];

// Dummy leave balance data for display
const DUMMY_LEAVE_BALANCES = [
  { id: 1, leaveTypeName: "Casual Leave", year: 2025, entitledDays: 12, usedDays: 3, balanceDays: 9, carriedForwardDays: 0 },
  { id: 2, leaveTypeName: "Sick Leave", year: 2025, entitledDays: 10, usedDays: 2, balanceDays: 8, carriedForwardDays: 0 },
  { id: 3, leaveTypeName: "Earned Leave", year: 2025, entitledDays: 15, usedDays: 5, balanceDays: 10, carriedForwardDays: 2 },
  { id: 4, leaveTypeName: "Compensatory Off", year: 2025, entitledDays: 5, usedDays: 1, balanceDays: 4, carriedForwardDays: 0 },
];

export default function EmployeeViewPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();

  const { fetchEmployeeById } = useEmployee();
  const [loading, setLoading] = useState(true);
  const [employee, setEmployee] = useState(null);
  const [bankDetails, setBankDetails] = useState([]);
  const [shiftPreferences, setShiftPreferences] = useState(null);
  const [assignedStations, setAssignedStations] = useState([]);
  const [payrollInfo, setPayrollInfo] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [leaveBalances, setLeaveBalances] = useState([]);
  const [activeTab, setActiveTab] = useState("basic");

  useEffect(() => {
    const load = async () => {
      try {
        const data = await fetchEmployeeById(id);
        setEmployee(data);

        const [bankData, shiftData, stationData, payrollData, docsData, leaveData] = await Promise.all([
          employeeService.getBankDetails(id).catch(() => []),
          employeeService.getShiftPreferences(id).catch(() => null),
          employeeService.getAssignedStations(id).catch(() => []),
          employeeService.getPayrollInfo(id).catch(() => null),
          employeeService.getDocuments(id).catch(() => []),
          employeeService.getLeaveBalances(id).catch(() => []),
        ]);

        setBankDetails(bankData || []);
        setShiftPreferences(shiftData);
        setAssignedStations(stationData || []);
        setPayrollInfo(payrollData);
        setDocuments(docsData || []);
        setLeaveBalances(leaveData || []);
      } catch (err) {
        console.error("Failed to load employee:", err);
      }
      setLoading(false);
    };
    load();
  }, [id]);

  const getBackPath = () => {
    return location.pathname.startsWith('/hrm') ? '/hrm/employees' : '/admin/employee';
  };

  const getEditPath = () => {
    return location.pathname.startsWith('/hrm')
      ? `/hrm/employees/edit/${id}`
      : `/admin/employee/edit/${id}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-teal-600 mx-auto" />
          <p className="mt-3 text-slate-600 font-medium">Loading employee data...</p>
        </div>
      </div>
    );
  }

  if (!employee) {
    return (
      <div className="flex flex-col items-center justify-center h-64">
        <p className="text-gray-500">Employee not found</p>
        <button
          onClick={() => navigate(getBackPath())}
          className="mt-4 px-4 py-2 text-teal-600 hover:underline"
        >
          Go back to list
        </button>
      </div>
    );
  }

  const addr = employee.address?.[0] || {};
  const role = employee.roles?.[0]?.name || "—";

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const formatCurrency = (amount) => {
    if (!amount) return "—";
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  const getStatusColor = (status) => {
    const colors = {
      ACTIVE: "bg-green-100 text-green-700",
      PROBATION: "bg-yellow-100 text-yellow-700",
      NOTICE_PERIOD: "bg-orange-100 text-orange-700",
      ON_LEAVE: "bg-blue-100 text-blue-700",
      SUSPENDED: "bg-red-100 text-red-700",
      TERMINATED: "bg-gray-100 text-gray-700",
      RESIGNED: "bg-gray-100 text-gray-700",
    };
    return colors[status] || "bg-gray-100 text-gray-700";
  };

  const getEmploymentTypeLabel = (type) => {
    const labels = {
      FULL_TIME: "Full Time",
      PART_TIME: "Part Time",
      CONTRACT: "Contract",
      TEMPORARY: "Temporary",
      INTERN: "Intern",
      FREELANCE: "Freelance",
    };
    return labels[type] || type || "—";
  };

  const getShiftLabel = (shift) => {
    const labels = {
      MORNING: "Morning Shift",
      AFTERNOON: "Afternoon Shift",
      EVENING: "Evening Shift",
      NIGHT: "Night Shift",
      SPLIT: "Split Shift",
      ROTATIONAL: "Rotational",
    };
    return labels[shift] || shift || "—";
  };

  return (
    <div className="flex flex-col h-full bg-gradient-to-br from-slate-50 via-white to-slate-50 overflow-x-hidden">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-slate-200/60 px-4 py-4 sticky top-0 z-20">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(getBackPath())}
              className="p-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-600 transition-all duration-200"
            >
              <ArrowLeft size={18} />
            </button>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-xl font-bold text-slate-900">Employee Details</h1>
                {employee.employeeCode && (
                  <span className="px-2 py-0.5 bg-teal-100 text-teal-700 text-xs font-medium rounded-full">
                    {employee.employeeCode}
                  </span>
                )}
              </div>
              <p className="text-sm text-slate-500 mt-0.5">
                {employee.firstName} {employee.lastName} • {employee.designation || role}
              </p>
            </div>
          </div>

          <button
            onClick={() => navigate(getEditPath())}
            className="px-5 py-2.5 bg-teal-600 text-white rounded-xl hover:bg-teal-700 flex items-center gap-2 text-sm font-semibold transition-all duration-200"
          >
            <Edit size={16} />
            Edit Employee
          </button>
        </div>
      </div>

      {/* Tab Navigation - Classic Style */}
      <div className="bg-white border-b border-slate-200 sticky top-[73px] z-10">
        <div className="flex">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-all duration-200 ${
                  isActive
                    ? "border-teal-600 text-teal-700 bg-teal-50/50"
                    : "border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50"
                }`}
              >
                <Icon size={15} />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden">
        <div className="px-4 py-4">
          <div className="space-y-6">

            {/* Basic Info Tab */}
            {activeTab === "basic" && (
              <div className="space-y-6">
                {/* Profile Header Card */}
                <div className="bg-white rounded-2xl border border-slate-200/60 overflow-hidden p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-20 h-20 rounded-full bg-teal-600 flex items-center justify-center text-white text-2xl font-bold">
                      {employee.firstName?.charAt(0)}{employee.lastName?.charAt(0) || ""}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-1">
                        <h2 className="text-xl font-bold text-gray-900">
                          {employee.firstName} {employee.lastName}
                        </h2>
                      </div>
                      <p className="text-sm text-gray-600 mb-2">
                        {employee.designation || role}
                        {employee.designation && role !== "—" && ` • ${role}`}
                      </p>
                      <div className="flex flex-wrap items-center gap-4">
                        <div className="flex items-center gap-1.5 text-sm text-gray-600">
                          <Mail size={14} className="text-gray-400" />
                          {employee.email || "—"}
                        </div>
                        <div className="flex items-center gap-1.5 text-sm text-gray-600">
                          <Phone size={14} className="text-gray-400" />
                          {employee.phoneNumber || "—"}
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                        employee.employmentStatus
                          ? getStatusColor(employee.employmentStatus)
                          : employee.active !== false
                            ? "bg-green-100 text-green-700"
                            : "bg-red-100 text-red-700"
                      }`}>
                        {employee.employmentStatus
                          ? employee.employmentStatus.replace("_", " ")
                          : employee.active !== false ? "Active" : "Inactive"}
                      </div>
                      {employee.employmentType && (
                        <div className="px-3 py-1 bg-slate-100 text-slate-600 rounded-full text-xs font-medium">
                          {getEmploymentTypeLabel(employee.employmentType)}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <ViewCard icon={User} title="Personal Information">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
                    <InfoItem label="First Name" value={employee.firstName} />
                    <InfoItem label="Last Name" value={employee.lastName} />
                    <InfoItem label="Email" value={employee.email} />
                    <InfoItem label="Phone" value={employee.phoneNumber} />
                    <InfoItem label="Secondary Phone" value={employee.secondaryPhone} />
                    <InfoItem label="Gender" value={employee.gender} />
                    <InfoItem label="Date of Birth" value={formatDate(employee.dateOfBirth)} />
                    <InfoItem label="Employee Code" value={employee.employeeCode} />
                  </div>
                </ViewCard>
              </div>
            )}

            {/* Employment Tab */}
            {activeTab === "employment" && (
              <ViewCard icon={Briefcase} title="Employment Details">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
                  <InfoItem label="Designation" value={employee.designation} />
                  <InfoItem label="Employment Type" value={getEmploymentTypeLabel(employee.employmentType)} />
                  <InfoItem label="Employment Status" value={employee.employmentStatus?.replace("_", " ")} />
                  <InfoItem label="Date of Joining" value={formatDate(employee.dateOfJoining)} />
                  <InfoItem label="Total Experience" value={employee.totalExperience} />
                  <InfoItem label="Role" value={role} />
                  <InfoItem label="User Type" value={employee.userType} />
                  <InfoItem label="Username" value={employee.username} />
                </div>
              </ViewCard>
            )}

            {/* Address & Emergency Tab */}
            {activeTab === "address" && (
              <div className="space-y-6">
                <ViewCard icon={MapPin} title="Address Information">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
                    <InfoItem label="Address Line 1" value={addr.address1} />
                    <InfoItem label="Address Line 2" value={addr.address2} />
                    <InfoItem label="City" value={addr.city} />
                    <InfoItem label="State" value={addr.state} />
                    <InfoItem label="Country" value={addr.country} />
                    <InfoItem label="Zip Code" value={addr.zipcode} />
                  </div>
                </ViewCard>

                <ViewCard icon={Phone} title="Emergency Contact">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
                    <InfoItem label="Contact Name" value={employee.emergencyContactName} />
                    <InfoItem label="Contact Phone" value={employee.emergencyPhoneNumber} />
                    <InfoItem label="Relationship" value={employee.emergencyContactRelation} />
                  </div>
                </ViewCard>
              </div>
            )}

            {/* Auth & Access Tab */}
            {activeTab === "auth" && (
              <div className="space-y-6">
                <ViewCard icon={ShieldCheck} title="Login Credentials">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
                    <InfoItem label="Username" value={employee.username} />
                    <InfoItem label="Password" value="••••••••" />
                    <InfoItem label="Role" value={role} />
                  </div>
                </ViewCard>

                <ViewCard icon={ShieldCheck} title="POS Access Control">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
                    <InfoItem label="PIN Code" value={employee.pinCode ? "••••" : "Not Set"} />
                    <InfoItem label="Max Discount %" value={employee.maxDiscountPercent ? `${employee.maxDiscountPercent}%` : "—"} />
                  </div>
                  <div className="mt-5 pt-5 border-t border-slate-100">
                    <label className="block text-sm font-medium text-slate-700 mb-3">Permissions</label>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                      <AccessBadge label="POS Access" enabled={employee.canAccessPos} />
                      <AccessBadge label="Void Orders" enabled={employee.canVoidOrders} />
                      <AccessBadge label="Refund" enabled={employee.canRefund} />
                      <AccessBadge label="Cash Drawer" enabled={employee.canOpenCashDrawer} />
                      <AccessBadge label="Apply Discount" enabled={employee.canApplyDiscount} />
                    </div>
                  </div>
                </ViewCard>
              </div>
            )}

            {/* Bank Details Tab */}
            {activeTab === "bank" && (
              <ViewCard icon={CreditCard} title="Bank Account Details">
                {bankDetails.length > 0 ? (
                  <div className="space-y-4">
                    {bankDetails.map((bank, idx) => (
                      <div key={bank.id || idx} className="p-4 bg-gradient-to-r from-teal-50 to-cyan-50 rounded-xl border border-teal-100">
                        <div className="flex items-center justify-between mb-3">
                          <span className="font-semibold text-slate-800">{bank.bankName || "Bank Account"}</span>
                          {bank.isPrimary && (
                            <span className="px-2 py-0.5 bg-teal-100 text-teal-700 text-xs font-medium rounded-full">Primary</span>
                          )}
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                          <InfoItem label="Account Number" value={bank.accountNumber ? `••••${bank.accountNumber.slice(-4)}` : "—"} />
                          <InfoItem label="IFSC Code" value={bank.ifscCode} />
                          <InfoItem label="Account Holder" value={bank.accountHolderName} />
                          <InfoItem label="Branch Name" value={bank.branchName} />
                          <InfoItem label="Account Type" value={bank.accountType} />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <EmptyState message="No bank details added" />
                )}
              </ViewCard>
            )}

            {/* Documents Tab - Hidden for now */}
            {/* {activeTab === "documents" && (...)} */}

            {/* Payroll Tab */}
            {activeTab === "payroll" && (
              <div className="space-y-4">
                {payrollInfo ? (
                  (() => {
                    // Calculate salary components
                    const basic = parseFloat(payrollInfo.baseSalary || payrollInfo.basicSalary) || 0;
                    const hra = parseFloat(payrollInfo.hraAllowance) || 0;
                    const transport = parseFloat(payrollInfo.transportAllowance) || 0;
                    const meal = parseFloat(payrollInfo.mealAllowance) || 0;
                    const other = parseFloat(payrollInfo.otherAllowances) || 0;

                    const pfEmployee = parseFloat(payrollInfo.pfContribution) || 0;
                    const esiEmployee = parseFloat(payrollInfo.esiContribution) || 0;
                    const profTax = parseFloat(payrollInfo.professionalTax) || 0;
                    const tdsPercent = parseFloat(payrollInfo.tdsPercent) || 0;

                    const totalEarnings = basic + hra + transport + meal + other;
                    const tdsAmount = (totalEarnings * tdsPercent) / 100;
                    const totalDeductions = pfEmployee + esiEmployee + profTax + tdsAmount;
                    const monthlyGross = totalEarnings;
                    const monthlyNet = totalEarnings - totalDeductions;

                    // PF Calculation: 12% of Basic, max ₹1,800 (ceiling ₹15,000)
                    const PF_CEILING = 15000;
                    const pfBase = Math.min(basic, PF_CEILING);
                    const pfEmployer = pfBase * 0.12;

                    // ESI: 3.25% employer if gross <= ₹21,000
                    const esiEmployer = monthlyGross <= 21000 ? monthlyGross * 0.0325 : 0;

                    // Gratuity: (Basic × 15) / 26 per month = ~4.81% of Basic
                    const gratuity = parseFloat(payrollInfo.gratuityAmount) || (basic * 15 / 26 / 12);

                    const totalEmployerCost = pfEmployer + esiEmployer + gratuity;

                    const annualCTC = (monthlyGross + totalEmployerCost) * 12;
                    const monthlyCTC = monthlyGross + totalEmployerCost;

                    return (
                      <>
                        {/* Salary Summary - Compact Header */}
                        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                          <div className="bg-slate-50 px-4 py-3 border-b border-slate-200 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <IndianRupee size={16} className="text-teal-600" />
                              <span className="font-semibold text-slate-800">Salary Structure</span>
                            </div>
                            {payrollInfo.effectiveFrom && (
                              <span className="text-xs text-slate-500">Effective: {formatDate(payrollInfo.effectiveFrom)}</span>
                            )}
                          </div>
                          <div className="grid grid-cols-4 divide-x divide-slate-200">
                            <div className="p-4 text-center">
                              <p className="text-xs text-slate-500 mb-1">Annual CTC</p>
                              <p className="text-lg font-bold text-slate-800">{formatCurrency(annualCTC)}</p>
                            </div>
                            <div className="p-4 text-center">
                              <p className="text-xs text-slate-500 mb-1">Monthly CTC</p>
                              <p className="text-lg font-bold text-slate-800">{formatCurrency(monthlyCTC)}</p>
                            </div>
                            <div className="p-4 text-center">
                              <p className="text-xs text-slate-500 mb-1">Gross Salary</p>
                              <p className="text-lg font-bold text-teal-600">{formatCurrency(monthlyGross)}</p>
                            </div>
                            <div className="p-4 text-center bg-teal-50">
                              <p className="text-xs text-teal-600 mb-1">Net Take-Home</p>
                              <p className="text-lg font-bold text-teal-700">{formatCurrency(monthlyNet)}</p>
                            </div>
                          </div>
                        </div>

                        {/* Salary Breakdown Table - Single unified table */}
                        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                          <table className="w-full text-sm table-fixed">
                            <colgroup>
                              <col className="w-1/2" />
                              <col className="w-1/4" />
                              <col className="w-1/4" />
                            </colgroup>
                            <thead>
                              <tr className="bg-emerald-50 border-b border-emerald-200">
                                <th className="text-left px-4 py-2.5 font-semibold text-emerald-800">Earnings</th>
                                <th className="text-right px-4 py-2.5 font-semibold text-emerald-800">Monthly (₹)</th>
                                <th className="text-right px-4 py-2.5 font-semibold text-emerald-800">Annual (₹)</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                              <tr>
                                <td className="px-4 py-2 text-slate-600">Basic Salary</td>
                                <td className="px-4 py-2 text-right text-slate-800 font-medium">{formatCurrency(basic)}</td>
                                <td className="px-4 py-2 text-right text-slate-500">{formatCurrency(basic * 12)}</td>
                              </tr>
                              <tr>
                                <td className="px-4 py-2 text-slate-600">House Rent Allowance (HRA)</td>
                                <td className="px-4 py-2 text-right text-slate-800 font-medium">{formatCurrency(hra)}</td>
                                <td className="px-4 py-2 text-right text-slate-500">{formatCurrency(hra * 12)}</td>
                              </tr>
                              <tr>
                                <td className="px-4 py-2 text-slate-600">Transport Allowance</td>
                                <td className="px-4 py-2 text-right text-slate-800 font-medium">{formatCurrency(transport)}</td>
                                <td className="px-4 py-2 text-right text-slate-500">{formatCurrency(transport * 12)}</td>
                              </tr>
                              <tr>
                                <td className="px-4 py-2 text-slate-600">Meal Allowance</td>
                                <td className="px-4 py-2 text-right text-slate-800 font-medium">{formatCurrency(meal)}</td>
                                <td className="px-4 py-2 text-right text-slate-500">{formatCurrency(meal * 12)}</td>
                              </tr>
                              {other > 0 && (
                                <tr>
                                  <td className="px-4 py-2 text-slate-600">Other Allowances</td>
                                  <td className="px-4 py-2 text-right text-slate-800 font-medium">{formatCurrency(other)}</td>
                                  <td className="px-4 py-2 text-right text-slate-500">{formatCurrency(other * 12)}</td>
                                </tr>
                              )}
                              <tr className="bg-emerald-50 border-t border-emerald-200">
                                <td className="px-4 py-2.5 text-emerald-700 font-semibold">Gross Earnings (A)</td>
                                <td className="px-4 py-2.5 text-right text-emerald-700 font-bold">{formatCurrency(totalEarnings)}</td>
                                <td className="px-4 py-2.5 text-right text-emerald-600 font-semibold">{formatCurrency(totalEarnings * 12)}</td>
                              </tr>
                            </tbody>
                          </table>

                          <table className="w-full text-sm table-fixed border-t-2 border-slate-200">
                            <colgroup>
                              <col className="w-1/2" />
                              <col className="w-1/4" />
                              <col className="w-1/4" />
                            </colgroup>
                            <thead>
                              <tr className="bg-rose-50 border-b border-rose-200">
                                <th className="text-left px-4 py-2.5 font-semibold text-rose-800">Deductions</th>
                                <th className="text-right px-4 py-2.5 font-semibold text-rose-800">Monthly (₹)</th>
                                <th className="text-right px-4 py-2.5 font-semibold text-rose-800">Annual (₹)</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                              <tr>
                                <td className="px-4 py-2 text-slate-600">
                                  Provident Fund - Employee
                                  <span className="text-slate-400 text-xs ml-1">(12% of Basic, max ₹1,800)</span>
                                </td>
                                <td className="px-4 py-2 text-right text-slate-800 font-medium">{formatCurrency(pfEmployee)}</td>
                                <td className="px-4 py-2 text-right text-slate-500">{formatCurrency(pfEmployee * 12)}</td>
                              </tr>
                              <tr>
                                <td className="px-4 py-2 text-slate-600">
                                  ESI - Employee
                                  <span className="text-slate-400 text-xs ml-1">(0.75% of Gross)</span>
                                </td>
                                <td className="px-4 py-2 text-right text-slate-800 font-medium">{formatCurrency(esiEmployee)}</td>
                                <td className="px-4 py-2 text-right text-slate-500">{formatCurrency(esiEmployee * 12)}</td>
                              </tr>
                              <tr>
                                <td className="px-4 py-2 text-slate-600">Professional Tax</td>
                                <td className="px-4 py-2 text-right text-slate-800 font-medium">{formatCurrency(profTax)}</td>
                                <td className="px-4 py-2 text-right text-slate-500">{formatCurrency(profTax * 12)}</td>
                              </tr>
                              {tdsAmount > 0 && (
                                <tr>
                                  <td className="px-4 py-2 text-slate-600">
                                    TDS
                                    <span className="text-slate-400 text-xs ml-1">({tdsPercent}%)</span>
                                  </td>
                                  <td className="px-4 py-2 text-right text-slate-800 font-medium">{formatCurrency(tdsAmount)}</td>
                                  <td className="px-4 py-2 text-right text-slate-500">{formatCurrency(tdsAmount * 12)}</td>
                                </tr>
                              )}
                              <tr className="bg-rose-50 border-t border-rose-200">
                                <td className="px-4 py-2.5 text-rose-700 font-semibold">Total Deductions (B)</td>
                                <td className="px-4 py-2.5 text-right text-rose-700 font-bold">{formatCurrency(totalDeductions)}</td>
                                <td className="px-4 py-2.5 text-right text-rose-600 font-semibold">{formatCurrency(totalDeductions * 12)}</td>
                              </tr>
                            </tbody>
                          </table>

                          <table className="w-full text-sm table-fixed border-t-2 border-teal-300">
                            <colgroup>
                              <col className="w-1/2" />
                              <col className="w-1/4" />
                              <col className="w-1/4" />
                            </colgroup>
                            <tbody>
                              <tr className="bg-teal-50">
                                <td className="px-4 py-3 text-teal-800 font-bold">Net Take-Home (A - B)</td>
                                <td className="px-4 py-3 text-right text-teal-800 font-bold text-lg">{formatCurrency(monthlyNet)}</td>
                                <td className="px-4 py-3 text-right text-teal-700 font-bold">{formatCurrency(monthlyNet * 12)}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>

                        {/* Employer Contributions (Part of CTC) */}
                        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                          <table className="w-full text-sm table-fixed">
                            <colgroup>
                              <col className="w-1/2" />
                              <col className="w-1/4" />
                              <col className="w-1/4" />
                            </colgroup>
                            <thead>
                              <tr className="bg-blue-50 border-b border-blue-200">
                                <th className="text-left px-4 py-2.5 font-semibold text-blue-800">Employer Contributions (Part of CTC)</th>
                                <th className="text-right px-4 py-2.5 font-semibold text-blue-800">Monthly (₹)</th>
                                <th className="text-right px-4 py-2.5 font-semibold text-blue-800">Annual (₹)</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                              <tr>
                                <td className="px-4 py-2 text-slate-600">
                                  Provident Fund - Employer
                                  <span className="text-slate-400 text-xs ml-1">(12% of Basic, max ₹1,800)</span>
                                </td>
                                <td className="px-4 py-2 text-right text-slate-800 font-medium">{formatCurrency(pfEmployer)}</td>
                                <td className="px-4 py-2 text-right text-slate-500">{formatCurrency(pfEmployer * 12)}</td>
                              </tr>
                              <tr>
                                <td className="px-4 py-2 text-slate-600">
                                  ESI - Employer
                                  <span className="text-slate-400 text-xs ml-1">(3.25% of Gross)</span>
                                </td>
                                <td className="px-4 py-2 text-right text-slate-800 font-medium">{formatCurrency(esiEmployer)}</td>
                                <td className="px-4 py-2 text-right text-slate-500">{formatCurrency(esiEmployer * 12)}</td>
                              </tr>
                              <tr>
                                <td className="px-4 py-2 text-slate-600">
                                  Gratuity
                                  <span className="text-slate-400 text-xs ml-1">(4.81% of Basic)</span>
                                </td>
                                <td className="px-4 py-2 text-right text-slate-800 font-medium">{formatCurrency(gratuity)}</td>
                                <td className="px-4 py-2 text-right text-slate-500">{formatCurrency(gratuity * 12)}</td>
                              </tr>
                              <tr className="bg-blue-50 border-t border-blue-200">
                                <td className="px-4 py-2.5 text-blue-700 font-semibold">Total Employer Cost</td>
                                <td className="px-4 py-2.5 text-right text-blue-700 font-bold">{formatCurrency(totalEmployerCost)}</td>
                                <td className="px-4 py-2.5 text-right text-blue-600 font-semibold">{formatCurrency(totalEmployerCost * 12)}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>

                        {/* Statutory & Other Info */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {/* Statutory Numbers */}
                          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                            <div className="bg-slate-50 px-4 py-3 border-b border-slate-200">
                              <span className="font-semibold text-slate-700 text-sm">Statutory Information</span>
                            </div>
                            <div className="p-4 space-y-3 text-sm">
                              <div className="flex justify-between">
                                <span className="text-slate-500">PF Account No.</span>
                                <span className="text-slate-800 font-medium">{payrollInfo.pfAccountNumber || "—"}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-slate-500">ESI Number</span>
                                <span className="text-slate-800 font-medium">{payrollInfo.esiNumber || "—"}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-slate-500">UAN Number</span>
                                <span className="text-slate-800 font-medium">{payrollInfo.uanNumber || "—"}</span>
                              </div>
                            </div>
                          </div>

                          {/* Rate Info */}
                          <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
                            <div className="bg-slate-50 px-4 py-3 border-b border-slate-200">
                              <span className="font-semibold text-slate-700 text-sm">Rate Information</span>
                            </div>
                            <div className="p-4 space-y-3 text-sm">
                              <div className="flex justify-between">
                                <span className="text-slate-500">Salary Type</span>
                                <span className="text-slate-800 font-medium">{payrollInfo.salaryType || "Monthly"}</span>
                              </div>
                              <div className="flex justify-between">
                                <span className="text-slate-500">Pay Frequency</span>
                                <span className="text-slate-800 font-medium">{payrollInfo.payFrequency || "Monthly"}</span>
                              </div>
                              {payrollInfo.hourlyRate > 0 && (
                                <div className="flex justify-between">
                                  <span className="text-slate-500">Hourly Rate</span>
                                  <span className="text-slate-800 font-medium">{formatCurrency(payrollInfo.hourlyRate)}</span>
                                </div>
                              )}
                              {payrollInfo.dailyRate > 0 && (
                                <div className="flex justify-between">
                                  <span className="text-slate-500">Daily Rate</span>
                                  <span className="text-slate-800 font-medium">{formatCurrency(payrollInfo.dailyRate)}</span>
                                </div>
                              )}
                              <div className="flex justify-between">
                                <span className="text-slate-500">Overtime Eligible</span>
                                <span className={`font-medium ${payrollInfo.isOvertimeEligible ? "text-emerald-600" : "text-slate-400"}`}>
                                  {payrollInfo.isOvertimeEligible ? "Yes" : "No"}
                                </span>
                              </div>
                              {payrollInfo.isOvertimeEligible && payrollInfo.overtimeRate > 0 && (
                                <div className="flex justify-between">
                                  <span className="text-slate-500">Overtime Rate/Hr</span>
                                  <span className="text-slate-800 font-medium">{formatCurrency(payrollInfo.overtimeRate)}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </>
                    );
                  })()
                ) : (
                  <ViewCard icon={Wallet} title="Payroll Information">
                    <EmptyState message="No payroll information added" />
                  </ViewCard>
                )}
              </div>
            )}

            {/* Leave Balance Tab */}
            {activeTab === "leave" && (
              <ViewCard icon={CalendarDays} title="Leave Balance">
                {(() => {
                  // Use real data if available, otherwise use dummy data
                  const displayData = leaveBalances.length > 0 ? leaveBalances : DUMMY_LEAVE_BALANCES;
                  return (
                    <div className="space-y-4">
                      {leaveBalances.length === 0 && (
                        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                          <p className="text-xs text-amber-700">Showing sample data. Leave balances will be synced from HR system.</p>
                        </div>
                      )}
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        {displayData.map((leave) => {
                          const used = parseFloat(leave.usedDays) || 0;
                          const total = parseFloat(leave.entitledDays) || 0;
                          const remaining = parseFloat(leave.balanceDays) ?? (total - used);
                          const percentUsed = total > 0 ? (used / total) * 100 : 0;

                          return (
                            <div
                              key={leave.id}
                              className="p-4 bg-white rounded-xl border border-slate-200 hover:border-teal-200 transition-colors"
                            >
                              <div className="flex items-center justify-between mb-3">
                                <span className="text-sm font-semibold text-slate-800">
                                  {leave.leaveTypeName || leave.leaveType?.name || "Leave"}
                                </span>
                                <span className="text-xs text-slate-400">{leave.year}</span>
                              </div>

                              {/* Progress Bar */}
                              <div className="mb-3">
                                <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                                  <div
                                    className={`h-full transition-all duration-300 ${
                                      percentUsed > 80 ? "bg-rose-500" : percentUsed > 50 ? "bg-amber-500" : "bg-teal-500"
                                    }`}
                                    style={{ width: `${Math.min(percentUsed, 100)}%` }}
                                  />
                                </div>
                              </div>

                              <div className="grid grid-cols-3 gap-1 text-center">
                                <div>
                                  <p className="text-[10px] text-slate-400 uppercase">Entitled</p>
                                  <p className="text-sm font-bold text-slate-700">{total}</p>
                                </div>
                                <div>
                                  <p className="text-[10px] text-slate-400 uppercase">Used</p>
                                  <p className="text-sm font-bold text-amber-600">{used}</p>
                                </div>
                                <div>
                                  <p className="text-[10px] text-slate-400 uppercase">Balance</p>
                                  <p className={`text-sm font-bold ${remaining > 0 ? "text-teal-600" : "text-rose-600"}`}>
                                    {remaining}
                                  </p>
                                </div>
                              </div>

                              {leave.carriedForwardDays > 0 && (
                                <div className="mt-2 pt-2 border-t border-slate-100 text-[10px] text-slate-500 text-center">
                                  +{leave.carriedForwardDays} carried forward
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}
              </ViewCard>
            )}

            {/* Shift & Stations Tab */}
            {activeTab === "shift" && (
              <div className="space-y-6">
                <ViewCard icon={Clock} title="Shift Preferences">
                  {shiftPreferences ? (
                    <div className="space-y-5">
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
                        <InfoItem label="Preferred Shift" value={getShiftLabel(shiftPreferences.preferredShift)} />
                        <InfoItem label="Max Hours/Day" value={shiftPreferences.maxHoursPerDay} />
                        <InfoItem label="Max Hours/Week" value={shiftPreferences.maxHoursPerWeek} />
                      </div>
                      <div className="pt-5 border-t border-slate-100">
                        <label className="block text-sm font-medium text-slate-700 mb-3">Weekly Availability</label>
                        <div className="flex flex-wrap gap-1.5">
                          {["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"].map((day, idx) => {
                            const available = shiftPreferences[`available${day}`];
                            const isWeekend = idx >= 5;
                            return (
                              <div
                                key={day}
                                className={`w-12 h-12 rounded-lg flex flex-col items-center justify-center transition-all ${
                                  available
                                    ? isWeekend
                                      ? "bg-amber-50 border border-amber-200"
                                      : "bg-emerald-50 border border-emerald-200"
                                    : "bg-slate-50 border border-slate-200"
                                }`}
                              >
                                <span className={`text-[10px] font-medium uppercase ${
                                  available
                                    ? isWeekend ? "text-amber-600" : "text-emerald-600"
                                    : "text-slate-400"
                                }`}>
                                  {day.slice(0, 3)}
                                </span>
                                <span className={`text-lg ${
                                  available
                                    ? isWeekend ? "text-amber-500" : "text-emerald-500"
                                    : "text-slate-300"
                                }`}>
                                  {available ? "✓" : "−"}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <EmptyState message="No shift preferences set" />
                  )}
                </ViewCard>

                <ViewCard icon={Building2} title="Assigned Stations">
                  {assignedStations.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {assignedStations.map((station, idx) => (
                        <div
                          key={station.id || idx}
                          className="flex items-center justify-between p-4 bg-gradient-to-r from-teal-50 to-cyan-50 rounded-xl border border-teal-100"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-teal-100 text-teal-600 rounded-xl flex items-center justify-center">
                              <Building2 size={18} />
                            </div>
                            <div>
                              <p className="text-sm font-semibold text-slate-800">{station.kitchenStationName || "Unknown Station"}</p>
                              {station.isPrimary && (
                                <span className="text-xs text-teal-600 font-medium flex items-center gap-1">
                                  <CheckCircle size={12} /> Primary
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <EmptyState message="No stations assigned" />
                  )}
                </ViewCard>
              </div>
            )}

          </div>
        </div>
      </div>
    </div>
  );
}

/* View Card Component */
function ViewCard({ icon: Icon, title, children }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200/60 overflow-hidden">
      <div className="px-6 py-4 border-b border-slate-100 bg-gradient-to-r from-slate-50 to-white">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-teal-600 flex items-center justify-center">
            <Icon size={20} className="text-white" />
          </div>
          <h3 className="text-base font-semibold text-slate-800">{title}</h3>
        </div>
      </div>
      <div className="p-6">
        {children}
      </div>
    </div>
  );
}

/* Info Item Component */
function InfoItem({ label, value, highlight }) {
  return (
    <div>
      <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">{label}</p>
      <p className={`text-sm ${highlight ? "font-semibold text-teal-700" : "text-slate-900"}`}>
        {value || "—"}
      </p>
    </div>
  );
}

/* Access Badge Component */
function AccessBadge({ label, enabled }) {
  return (
    <div>
      <p className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-1">{label}</p>
      <div className={`flex items-center gap-1.5 text-sm ${enabled ? "text-green-600" : "text-slate-400"}`}>
        {enabled ? <CheckCircle size={14} /> : <XCircle size={14} />}
        <span>{enabled ? "Yes" : "No"}</span>
      </div>
    </div>
  );
}

/* Empty State Component */
function EmptyState({ message }) {
  return (
    <div className="py-12 text-center">
      <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
        <AlertCircle size={28} className="text-slate-400" />
      </div>
      <p className="text-slate-500">{message}</p>
    </div>
  );
}
